import React, {
	createContext,
	useContext,
	useState,
	useEffect,
	useCallback,
} from "react";
import { API_URL } from "../lib/utils";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
	const [user, setUser] = useState(null);
	const [loading, setLoading] = useState(true);

	const checkAuth = useCallback(async () => {
		try {
			const token = localStorage.getItem("session_token");
			const headers = {};
			if (token) {
				headers["Authorization"] = `Bearer ${token}`;
			}

			const response = await fetch(`${API_URL}/api/auth/me`, {
				credentials: "include",
				headers,
			});

			if (response.ok) {
				const userData = await response.json();
				setUser(userData);
			} else {
				setUser(null);
				localStorage.removeItem("session_token");
			}
		} catch (error) {
			console.error("Auth check failed:", error);
			setUser(null);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		checkAuth();
	}, [checkAuth]);

	// Sync auth state across tabs/windows
	useEffect(() => {
		const handleStorageChange = (e) => {
			if (e.key === "active_role_ping") {
				// Dual-role mode switch happened in another tab: the session token
				// is unchanged, so only a re-check surfaces the new active role.
				checkAuth();
				return;
			}
			if (e.key === "session_token") {
				if (!e.newValue) {
					// Token was removed in another tab → logout locally
					setUser(null);
				} else if (e.newValue !== e.oldValue) {
					// Token changed in another tab → re-check auth
					checkAuth();
				}
			}
		};
		window.addEventListener("storage", handleStorageChange);
		return () => window.removeEventListener("storage", handleStorageChange);
	}, [checkAuth]);

	const login = async (email, password) => {
		// Clear previous session before new login
		localStorage.removeItem("session_token");
		const response = await fetch(`${API_URL}/api/auth/login`, {
			method: "POST",
			credentials: "include",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ email, password }),
		});

		if (!response.ok) {
			let detail = "Login failed";
			try {
				const errorData = await response.json();
				detail = errorData.detail || detail;
			} catch {
				// Body already consumed or not valid JSON — use status code only
			}
			const err = new Error(detail);
			// Status 400 on /login means exclusively: Google-only account
			if (response.status === 400) {
				err.code = "GOOGLE_ONLY_ACCOUNT";
			}
			throw err;
		}

		const data = await response.json();
		localStorage.setItem("session_token", data.token);
		setUser(data);
		return data;
	};

	const register = async (
		name,
		email,
		password,
		gender = null,
		sport = null,
		surname = null,
	) => {
		// Clear previous session before new registration
		localStorage.removeItem("session_token");
		const response = await fetch(`${API_URL}/api/auth/register`, {
			method: "POST",
			credentials: "include",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				name,
				surname,
				email,
				password,
				gender,
				sport,
				role: "athlete",
			}),
		});

		if (!response.ok) {
			let detail = "Registration failed";
			try {
				const errorData = await response.json();
				detail = errorData.detail || detail;
			} catch {
				// Body already consumed or not valid JSON
			}
			const err = new Error(detail);
			if (response.status === 400 && detail.toLowerCase().includes("google")) {
				err.code = "GOOGLE_ONLY_ACCOUNT";
			}
			throw err;
		}

		const data = await response.json();
		localStorage.setItem("session_token", data.token);
		setUser(data);
		return data;
	};

	// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
	// Native Google OAuth - credential is passed from GoogleLogin component
	const loginWithGoogleCredential = async (credential, profileData = {}) => {
		// Clear previous session before new Google login
		localStorage.removeItem("session_token");
		const requestBody = {
			credential,
			gender: profileData?.gender || null,
			sport: profileData?.sport || null,
		};

		const response = await fetch(`${API_URL}/api/auth/google`, {
			method: "POST",
			credentials: "include",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(requestBody),
		});

		const data = await response.json();

		if (!response.ok) {
			throw new Error(data.detail || "Google login failed");
		}

		localStorage.setItem("session_token", data.session_token);
		setUser(data);
		return data;
	};

	const logout = async () => {
		try {
			await fetch(`${API_URL}/api/auth/logout`, {
				method: "POST",
				credentials: "include",
			});
		} catch (error) {
			console.error("Logout error:", error);
		}
		localStorage.removeItem("session_token");
		localStorage.removeItem("pending_invite_token");
		setUser(null);
	};

	const switchAccount = async (accountUserId) => {
		const token = localStorage.getItem("session_token");
		const headers = { "Content-Type": "application/json" };
		if (token) {
			headers["Authorization"] = `Bearer ${token}`;
		}

		const response = await fetch(`${API_URL}/api/auth/switch-account`, {
			method: "POST",
			credentials: "include",
			headers,
			body: JSON.stringify({ account_user_id: accountUserId }),
		});

		if (!response.ok) {
			let detail = "Account switch failed";
			try {
				const errorData = await response.json();
				detail = errorData.detail || detail;
			} catch {
				// Ignore invalid/non-JSON response body.
			}
			throw new Error(detail);
		}

		const data = await response.json();
		if (data?.token) {
			localStorage.setItem("session_token", data.token);
		}
		setUser(data);
		return data;
	};

	// Dual-role accounts (one user_id that is both coach and athlete) switch the
	// active mode here. Sibling of switchAccount, which moves between two linked
	// accounts instead. No new token: the selected mode lives on the user doc,
	// so the switch is global for the user exactly like switchAccount's cookie.
	const switchRole = async (nextRole) => {
		const token = localStorage.getItem("session_token");
		const headers = { "Content-Type": "application/json" };
		if (token) {
			headers["Authorization"] = `Bearer ${token}`;
		}

		const response = await fetch(`${API_URL}/api/auth/switch-role`, {
			method: "POST",
			credentials: "include",
			headers,
			body: JSON.stringify({ role: nextRole }),
		});

		if (!response.ok) {
			let detail = "Role switch failed";
			try {
				const errorData = await response.json();
				detail = errorData.detail || detail;
			} catch {
				// Ignore invalid/non-JSON response body.
			}
			throw new Error(detail);
		}

		const data = await response.json();
		setUser(data);
		// Notify the other open tabs: they listen on `storage` and re-check auth.
		localStorage.setItem("active_role_ping", String(Date.now()));
		return data;
	};

	const updateUser = (userData) => {
		setUser((prev) => ({ ...prev, ...userData }));
	};

	return (
		<AuthContext.Provider
			value={{
				user,
				loading,
				login,
				register,
				loginWithGoogleCredential,
				switchAccount,
				switchRole,
				logout,
				updateUser,
				checkAuth,
				setUser,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
}

export function useAuth() {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error("useAuth must be used within AuthProvider");
	}
	return context;
}
